import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { usePreferredFirstName } from 'hooks/use-preferred-first-name.hook';
import {
  DropdownSection,
  useUserDropdownConfig,
} from 'hooks/use-user-dropdown-config.hook';
import { useProductSegmentation } from 'hooks/use-product-segmentation.hook';

function useFilterUserDropdownConfig(
  config: DropdownSection[],
  isMobile: boolean,
): DropdownSection[] {
  const { formatMessage } = useIntl();
  const { showPharmacySettings, showFormsCenter } = useProductSegmentation();

  if (isMobile) return config;

  // Build dynamic allowed items based on product segmentation
  const allowedItems = [
    formatMessage({ id: 'STR_PERSONAL_INFO' }),
    formatMessage({ id: 'STR_SECURITY_SETTINGS' }),
    formatMessage({ id: 'STR_PDP_PREMIUM_PAYMENTS' }),
    formatMessage({ id: 'STR_SUPPLEMENTAL_PREMIUM_PAYMENT' }),
    formatMessage({ id: 'STR_MEDICARE_PREMIUM_PAYMENT' }),
    formatMessage({ id: 'STR_CUSTOMER_SUPPORT' }),
    formatMessage({ id: 'STR_ABOUT_CIGNA_HEALTHCARE' }),
    formatMessage({ id: 'STR_LANGUAGE_ASSISTANCE' }),
    formatMessage({ id: 'STR_DELETE_YOUR_ACCOUNT' }),
    formatMessage({ id: 'STR_LOG_OUT' }),
  ];

  // Add pharmacy settings conditionally based on product segmentation
  if (showPharmacySettings) {
    allowedItems.push(formatMessage({ id: 'STR_MANAGE_PHARMACY_SETTINGS' }));
  }

  // Add forms center conditionally based on product segmentation
  if (showFormsCenter) {
    allowedItems.push(formatMessage({ id: 'STR_FORMS_CENTER' }));
  }

  return config
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => allowedItems.includes(item.text)),
    }))
    .filter((section) => section.items.length > 0);
}

export const useUserDropdownComponent = (handleDeleteAccount: () => void) => {
  const { data, isLoading, error } = usePreferredFirstName();
  const userDropdownConfig = useUserDropdownConfig(handleDeleteAccount);
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));

  const filteredConfig = useFilterUserDropdownConfig(
    userDropdownConfig,
    isMobile,
  );

  const dropdownMenuItemProps = {
    preferredFirstName: data.preferredFirstName,
    menuItemsConfig: filteredConfig,
  };

  return { data: dropdownMenuItemProps, isLoading, error };
};
